import { useState } from "react";
import { mockBackups } from "@/mocks/dealerData";

export default function BackupPage() {
  const [backups, setBackups] = useState(mockBackups);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [backupProgress, setBackupProgress] = useState(0);

  const handleBackup = () => {
    setIsBackingUp(true);
    setBackupProgress(0);
    const interval = setInterval(() => {
      setBackupProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsBackingUp(false);
          const newBackup = {
            id: "b" + Date.now(),
            fileName: `ART_POS_backup_${new Date().toISOString().split("T")[0]}_${new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }).replace(":", "-")}.zip`,
            size: "24.8 MB",
            date: new Date().toISOString(),
            type: "manuel",
            status: "Başarılı",
          };
          setBackups((prev) => [newBackup, ...prev]);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
  };

  const handleRestore = (id: string) => {
    setRestoreId(id);
    setShowRestoreModal(true);
  };

  const confirmRestore = () => {
    alert("Sistem geri yükleniyor... (Demo)");
    setShowRestoreModal(false);
    setRestoreId(null);
  };

  const deleteBackup = (id: string) => {
    setBackups((prev) => prev.filter((b) => b.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="backup-title">Yedekleme</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Sistem verilerinizi yedekleyin ve geri yükleyin</p>
        </div>
        <button onClick={handleBackup} disabled={isBackingUp} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
          <i className={`${isBackingUp ? "ri-loader-4-line animate-spin" : "ri-database-2-line"}`}></i>
          {isBackingUp ? "Yedekleniyor..." : "Yeni Yedek Al"}
        </button>
      </div>

      {/* Backup Progress */}
      {isBackingUp && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground-900">Yedekleme yapılıyor...</span>
            <span className="text-sm text-foreground-500">{Math.min(100, Math.round(backupProgress))}%</span>
          </div>
          <div className="w-full bg-background-200/70 rounded-full h-2">
            <div className="bg-primary-500 h-2 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, backupProgress)}%` }}></div>
          </div>
        </div>
      )}

      {/* Auto backup info */}
      <div className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-xl p-4 mb-6">
        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
          <i className="ri-time-line text-primary-600"></i>
        </div>
        <div>
          <p className="text-sm font-medium text-foreground-900">Otomatik Yedekleme</p>
          <p className="text-xs text-foreground-500">Sistem her gece saat 23:00'te otomatik yedek alır. Son 30 günlük yedekler saklanır.</p>
        </div>
      </div>

      {/* Backup List */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
          <h4 className="font-medium text-foreground-900 text-sm"><a id="backup-list">Yedekleme Geçmişi</a></h4>
          <span className="text-xs text-foreground-400">{backups.length} yedek</span>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Dosya Adı</th>
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tarih</th>
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Boyut</th>
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tür</th>
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Durum</th>
                <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-900 text-xs">{b.fileName}</td>
                  <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(b.date).toLocaleString("en-US")}</td>
                  <td className="px-4 py-3 text-foreground-600">{b.size}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${b.type === "otomatik" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                      {b.type === "otomatik" ? "Otomatik" : "Manuel"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                      <i className="ri-check-line"></i> {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleRestore(b.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Geri Yükle">
                        <i className="ri-refresh-line text-sm"></i>
                      </button>
                      <button onClick={() => deleteBackup(b.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer" title="Sil">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-background-200/70">
          {backups.map((b) => (
            <div key={b.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate">{b.fileName}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-400">{new Date(b.date).toLocaleString("en-US")}</span>
                    <span className="text-xs text-foreground-500">{b.size}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${b.type === "otomatik" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"}`}>
                      {b.type === "otomatik" ? "Otomatik" : "Manuel"}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                      <i className="ri-check-line"></i> {b.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleRestore(b.id)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Geri Yükle">
                    <i className="ri-refresh-line"></i>
                  </button>
                  <button onClick={() => deleteBackup(b.id)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer" title="Sil">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowRestoreModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-alert-line text-accent-600 text-xl"></i>
              </div>
              <h4 className="text-base font-semibold text-foreground-950"><a id="restore-confirm">Geri Yüklemeyi Onayla</a></h4>
              <p className="text-sm text-foreground-500 mt-2">
                Bu işlem mevcut tüm verilerinizi seçilen yedekteki verilerle değiştirecektir. Bu işlem geri alınamaz!
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowRestoreModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={confirmRestore} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Geri Yükle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}